/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import axios, { AxiosError } from 'axios';
import { FC, useState, useRef, useEffect } from 'react';
import { useFormContext } from '../../../../../contexts/FormContext';
import { useOutputAreaContext } from '../../../../../contexts/OuputAreaContext';
import { useResponseHistoryContext } from '../../../../../contexts/ResponseHistoryContext';
import { APIErrors } from '../../../../../types/API';
import { FormType, GeneratedResponse } from '../../../../../types/FormTypes';
import { BasicButton } from '../../../../shared/buttons/BasicButton';
import Image from 'next/image';
import styles from './OutputButtons.module.css';
import { marked } from 'marked';

export const OutputButtons: FC = () => {
  const {
    formType,
    blogInput,
    personaBlogInput,
    emailInput,
    personaEmailInput,
    personaSocialInput,
    personaNewsletterInput,
    socialInput,
    marketingIdeasInput,
    personaMarketingIdeasInput,
    contentImproverInput,
    audienceRetargetInput,
    audienceChangerInput,
    formatChangerInput,
    toneChangerInput,
    seasonalHolidayInput,
    discountPromotionInput,
    giveawayPromotionInput,
    linkedinCompanyBioInput,
    youtubeChannelDescriptionInput,
    tiktokCaptionInput,
    productDescriptionInput,
    valuePropInput,
    youtubeVideoDescriptionInput,
    shortVideoScriptInput,
    brandMissionInput,
    youtubeVideoScriptInput,
    jobDescriptionInput,

    editedOutput,
    setEditedOutput,
    setGeneratedOutput,
    canKeepGoing,
    setCanKeepGoing,
    loading,
    setLoading,
  } = useFormContext();
  const { likeResponse, dislikeResponse, favoriteResponse } =
    useResponseHistoryContext();

  const { responseRated, copyResponse, saveFileClicked } =
    useOutputAreaContext();

  const [showExportMenu, setShowExportMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: { target: any }) => {
      console.log(e.target);
      console.log(menuRef.current?.contains(e.target));
      if (!menuRef.current?.contains(e.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
    };
  });

  let markText = '';
  // Function to generate and export the markdown content as a PDF with a banner
  const handleExportPDF = async () => {
    setLoading(true);
    const text = editedOutput[0].text
      .replace(/^"|"$/g, '')
      .replace(/\\n/g, '\n');
    markText = await marked(text);
    const response = await axios.post(
      '/api/exporting/puppeteerForPDF',
      { html: markText },
      {
        responseType: 'blob', // Expecting a file (PDF) as a response
      }
    );

    // Create a Blob and save the PDF
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'generated-pdf.pdf'); // Downloadable PDF name
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setLoading(false);
  };

  const sendHtmlForDocx = async () => {
    try {
      setLoading(true);
      const text = editedOutput[0].text
        .replace(/^"|"$/g, '')
        .replace(/\\n/g, '\n');
      markText = await marked(text);

      // Send the HTML content to the backend API
      const response = await axios.post(
        '/api/exporting/exportToDocX',
        { html: markText },
        {
          responseType: 'blob', // Important to get the file as binary data
        }
      );

      // Create a Blob from the response data
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });

      // Create a link element to download the file
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'generated-docx.docx'); // Set the download file name
      document.body.appendChild(link);
      link.click();
      setLoading(false);

      // Clean up and remove the link element
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error generating DOCX:', error);
    }
  };

  // Function to generate and download a TXT file from HTML
  const handleExportTXT = async () => {
    setLoading(true);
    const text = editedOutput[0].text
      .replace(/^"|"$/g, '')
      .replace(/\\n/g, '\n');
    markText = await marked(text);
    // Create a DOM element from the HTML content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = markText;

    // Extract plain text from the HTML
    const textContent = tempDiv.textContent || tempDiv.innerText || '';

    // Create a Blob with the plain text
    const blob = new Blob([textContent], { type: 'text/plain' });

    // Create a link to trigger the download
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'generated-text.txt'; // Set the download file name

    // Append the link to the document body and trigger the download
    document.body.appendChild(link);
    link.click();

    // Clean up by removing the link from the document
    document.body.removeChild(link);
    setShowExportMenu(false);
    setLoading(false);
  };

  const handleExportMarkdown = async () => {
    setLoading(true);
    setLoading(true);
    const text = editedOutput[0].text
      .replace(/^"|"$/g, '')
      .replace(/\\n/g, '\n');
    markText = await marked(text);
    // Create a DOM element from the HTML content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = markText;

    // Create a Blob with the Markdown content
    const blob = new Blob([markText], { type: 'text/markdown;charset=utf-8' });

    // Create a link to trigger the download
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'generated-markdown.md'; // Set the download file name

    // Append the link to the document body and trigger the download
    document.body.appendChild(link);
    link.click();

    // Clean up by removing the link from the document
    document.body.removeChild(link);
    setShowExportMenu(false);
    setLoading(false);
  };

  const keepGoing = async () => {
    if (
      !blogInput ||
      !socialInput ||
      !emailInput ||
      !personaEmailInput ||
      !personaBlogInput ||
      !personaSocialInput ||
      !personaNewsletterInput ||
      !audienceChangerInput ||
      !formatChangerInput ||
      !toneChangerInput ||
      !seasonalHolidayInput ||
      !discountPromotionInput ||
      !giveawayPromotionInput ||
      !linkedinCompanyBioInput ||
      !youtubeChannelDescriptionInput ||
      !tiktokCaptionInput ||
      !productDescriptionInput ||
      !valuePropInput ||
      !youtubeVideoDescriptionInput ||
      !brandMissionInput ||
      !youtubeVideoScriptInput ||
      !shortVideoScriptInput ||
      !jobDescriptionInput
    )
      return;
    setLoading(true);
    let values = {};
    let apiEndpoint = '/api/openai/';
    switch (formType) {
      case FormType.BLOG:
        values = blogInput;
        apiEndpoint += '/generateBlog';
        break;
      case FormType.PERSONABLOG:
        values = personaBlogInput;
        apiEndpoint += '/generatePersonaBlog';
        break;
      case FormType.EMAIL:
        values = emailInput;
        apiEndpoint += '/generateEmail';
        break;
      case FormType.PERSONAEMAIL:
        values = personaEmailInput;
        apiEndpoint += '/generatePersonaEmail';
        break;
      case FormType.SOCIAL:
      default:
        values = socialInput;
        apiEndpoint += '/generateSocial';
        break;
      case FormType.PERSONA:
        values = personaSocialInput;
        apiEndpoint += 'generatePersonaSocial';
        break;
      case FormType.PERSONA_INSTAGRAM:
        values = personaSocialInput;
        apiEndpoint += 'generatePersonaSocial';
        break;
      case FormType.PERSONA_FACEBOOK:
        values = personaSocialInput;
        apiEndpoint += 'generatePersonaSocial';
        break;
      case FormType.PERSONA_TWITTER:
        values = personaSocialInput;
        apiEndpoint += 'generatePersonaSocial';
        break;
      case FormType.PERSONA_LINKEDIN:
        values = personaSocialInput;
        apiEndpoint += 'generatePersonaSocial';
        break;
      case FormType.TIKTOK:
        values = tiktokCaptionInput;
        apiEndpoint += 'generateTikTokCaption';
        break;
      case FormType.MARKETINGIDEAS:
        values = marketingIdeasInput;
        apiEndpoint += '/generateMarketingIdeas';
        break;
      case FormType.PERSONAMARKETINGIDEAS:
        values = personaMarketingIdeasInput;
        apiEndpoint += '/generatePersonaMarketingIdeas';
        break;
      case FormType.PERSONA_NEWSLETTER:
        values = personaNewsletterInput;
        apiEndpoint += 'generatePersonaNewsletter';
        break;
      case FormType.PROMOTIONAL_NEWSLETTER:
        values = personaNewsletterInput;
        apiEndpoint += 'generatePersonaNewsletter';
        break;
      case FormType.PRODUCT_UPDATE_NEWSLETTER:
        values = personaNewsletterInput;
        apiEndpoint += 'generatePersonaNewsletter';
        break;
      case FormType.EDUCATIONAL_NEWSLETTER:
        values = personaNewsletterInput;
        apiEndpoint += 'generatePersonaNewsletter';
        break;
      case FormType.ORGANIZATIONAL_NEWSLETTER:
        values = personaNewsletterInput;
        apiEndpoint += 'generatePersonaNewsletter';
        break;
      case FormType.CONTENT_IMPROVER:
        values = contentImproverInput;
        apiEndpoint += '/improveContent';
        break;
      case FormType.CONTENT_CONCISER:
        values = contentImproverInput;
        apiEndpoint += '/improveContent';
        break;
      case FormType.CONTENT_EXPANDER:
        values = contentImproverInput;
        apiEndpoint += '/improveContent';
        break;
      case FormType.CONTENT_ENGAGER:
        values = contentImproverInput;
        apiEndpoint += '/improveContent';
        break;
      case FormType.AUDIENCE_RETARGET:
        values = audienceRetargetInput;
        apiEndpoint += '/retargetAudience';
        break;
      case FormType.AUDIENCE_CHANGER:
        values = audienceChangerInput;
        apiEndpoint += '/generateAudienceChanger';
        break;
      case FormType.FORMAT_CHANGER:
        values = formatChangerInput;
        apiEndpoint += '/generateFormatChanger';
        break;
      case FormType.TONE_CHANGER:
        values = toneChangerInput;
        apiEndpoint += '/generateToneChanger';
        break;
      case FormType.SEASONAL:
        values = seasonalHolidayInput;
        apiEndpoint += '/generateSeasonalHoliday';
        break;
      case FormType.DISCOUNT:
        values = discountPromotionInput;
        apiEndpoint += '/generateDiscountPromotion';
        break;
      case FormType.GIVEAWAY:
        values = giveawayPromotionInput;
        apiEndpoint += '/generateGiveAwayPromotion';
        break;
      case FormType.LINKEDIN_COMPANY_BIO:
        values = linkedinCompanyBioInput;
        apiEndpoint += '/generateLinkedInCompanyBio';
        break;
      case FormType.YOUTUBE_CHANNEL_DESCRIPTION:
        values = youtubeChannelDescriptionInput;
        apiEndpoint += '/generateYoutubeChannelDescription';
        break;
      case FormType.PRODUCT_DESCRIPTION:
        values = productDescriptionInput;
        apiEndpoint += '/generateProductDescription';
        break;
      case FormType.VALUE_PROP:
        values = valuePropInput;
        apiEndpoint += '/generateValueProp';
        break;
      case FormType.YOUTUBE_VIDEO_DESCRIPTION:
        values = youtubeVideoDescriptionInput;
        apiEndpoint += '/generateYoutubeVideoDescription';
        break;
      case FormType.BRAND_MISSION:
        values = brandMissionInput;
        apiEndpoint += '/generateBrandMission';
        break;
      case FormType.YOUTUBE_VIDEO_SCRIPT:
        values = youtubeVideoScriptInput;
        apiEndpoint += '/generateYoutubeVideoScript';
        break;
      case FormType.SHORT_VIDEO_SCRIPT:
        values = shortVideoScriptInput;
        apiEndpoint += '/generateShortVideoScript';
        break;
      case FormType.JOB_DESCRIPTION:
        values = jobDescriptionInput;
        apiEndpoint += '/generateJobDescription';
        break;
    }

    try {
      const { data: generatedResponseData } = await axios.post<
        GeneratedResponse[]
      >(apiEndpoint, {
        ...values,
        prevResponse: editedOutput[0].text,
      });
      const output: GeneratedResponse[] =
        generatedResponseData.length > 0
          ? generatedResponseData
          : [
              {
                text: '⚠️ Something went wrong when generating your response, please try again.',
                canKeepGoing: false,
              },
            ];
      setEditedOutput(output);
      setGeneratedOutput(output);
      setCanKeepGoing(generatedResponseData[0].canKeepGoing);
    } catch (error) {
      const err = error as AxiosError;
      const errorData = err.response?.data as APIErrors;
      const errorMessage =
        typeof errorData?.error === 'string' && errorData?.error
          ? errorData.error
          : 'Something went wrong, please try again';
      setEditedOutput([{ text: errorMessage, canKeepGoing: false }]);
      setGeneratedOutput([{ text: errorMessage, canKeepGoing: false }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {editedOutput.length < 2 && (
        <BasicButton
          type="button"
          disabled={loading}
          tooltip="Copy"
          onClick={() => {
            copyResponse();
          }}
          text="Copy"
          image={{
            src: '/icons/CopyClear.svg',
            height: 16,
            width: 16,
          }}
          extraStyles={{
            backgroundColor: 'var(--ea-white)',
          }}
        />
      )}
      {canKeepGoing && editedOutput.length < 2 && (
        <BasicButton
          type="button"
          disabled={loading}
          onClick={() => {
            keepGoing();
          }}
          text={loading ? 'Generating...' : 'Keep Going'}
          image={{
            src: '/icons/ArrowGoingUpBlack.svg',
            height: 16,
            width: 16,
          }}
          extraStyles={{
            backgroundColor: 'var(--ea-white)',
          }}
        />
      )}
      {editedOutput.length < 2 && (
        <BasicButton
          type="button"
          disabled={!editedOutput || loading || responseRated}
          image={{
            src: !responseRated
              ? loading
                ? '/demoGifs/animation.gif'
                : '/icons/Heart.svg'
              : '/icons/HeartRed.svg',
            height: 16,
            width: 16,
          }}
          tooltip="Favorite Response"
          onClick={async () => {
            setLoading(true);
            await favoriteResponse(0);
            setLoading(false);
          }}
          extraStyles={{
            backgroundColor: 'var(--ea-white)',
          }}
        />
      )}
      {editedOutput.length < 2 && (
        <BasicButton
          type="button"
          disabled={!editedOutput || loading || responseRated}
          image={{
            src: loading ? '/demoGifs/animation.gif' : '/icons/Export.svg',
            height: 16,
            width: 16,
          }}
          tooltip="Export"
          onClick={() => {
            setShowExportMenu(!showExportMenu);
          }}
          extraStyles={{
            backgroundColor: 'var(--ea-white)',
          }}
        />
      )}
      {showExportMenu && (
        <div className={styles['dropdown-content2']} ref={menuRef}>
          <div className={styles['dropdown-content-item2']} onClick={() => {}}>
            <p>Pick an export format:</p>
          </div>
          {/* <div
            className={styles['dropdown-content-item']}
            onClick={async () => {
              await handleExportPDF();
            }}
          >
            <Image
              src={loading ? '/demoGifs/animation.gif' : '/icons/PDF Icon.svg'}
              alt="Close"
              layout="fixed"
              objectFit="contain"
              height={16}
              width={16}
            />
            <p>PDF Standard</p>
          </div> */}

          <div
            className={styles['dropdown-content-item']}
            onClick={async () => {
              await sendHtmlForDocx();
            }}
          >
            <Image
              src={loading ? '/demoGifs/animation.gif' : '/icons/Word Icon.svg'}
              alt="Close"
              layout="fixed"
              objectFit="contain"
              height={16}
              width={16}
            />
            <p>Word Document</p>
          </div>

          <div
            className={styles['dropdown-content-item']}
            onClick={async () => {
              await handleExportMarkdown();
            }}
          >
            <Image
              src={loading ? '/demoGifs/animation.gif' : '/icons/Scroll.svg'}
              alt="Close"
              layout="fixed"
              objectFit="contain"
              height={16}
              width={16}
            />
            <p>Markdown</p>
          </div>
          <div
            className={styles['dropdown-content-item']}
            onClick={async () => {
              await handleExportTXT();
            }}
          >
            <Image
              src={loading ? '/demoGifs/animation.gif' : '/icons/Scroll.svg'}
              alt="Close"
              layout="fixed"
              objectFit="contain"
              height={16}
              width={16}
            />
            <p>Plain Text</p>
          </div>
        </div>
      )}

      {editedOutput.length < 2 && (
        <BasicButton
          type="button"
          disabled={!editedOutput || loading || responseRated}
          image={{
            src: '/icons/Folder2.svg',
            height: 16,
            width: 16,
          }}
          tooltip="Save to Folder"
          onClick={() => {
            saveFileClicked(editedOutput[0].id ?? '');
          }}
          extraStyles={{
            backgroundColor: 'var(--ea-white)',
          }}
        />
      )}
    </>
  );
};
